import { IconButton } from "@mui/material";
import React, { Fragment, useContext } from "react";
import { FiBox } from "react-icons/fi";

import { Package, ProductRate } from "../../../../logic/api";

import { SessionContext } from "../../../../utility/useSession";
import { formatCurrency } from "../../../../utility/utils";
import { useDate } from "../../../../utility/useDate";

import flex from "../../../../styles/flex";
import styles from "../PackageListItem/styles";

import { calculatePackagePrice, PackageAvailability } from "../../../packages/builder/PackageBuilder";
import { Trans } from "react-i18next";
import { ReleaseFlag, useReleaseManagement } from "@/utility/feature-management";
import { t } from "i18next";


const PackageListItemSelector: React.FunctionComponent<{
  selected: boolean,
  package: Package,
  onSelectPackage: (pkg: Package) => void

}> = (props) => {
  const pkg = props.package;
  const { toFormattedDateString, localizeDate } = useDate();
  const session = useContext(SessionContext);

  const isCapProductsPerReturnDateReleased = useReleaseManagement(ReleaseFlag.releaseCapProductsSoldPerReturnDate);

  if (session.current.site == null) {
    return <Fragment />
  }

  let availability: PackageAvailability = PackageAvailability.available;
  let iconColour = "black";

  const localizedStartOfDay = localizeDate(new Date())
    .set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
  const localizedStartOfPackageStartDate = localizeDate(new Date(pkg.startDate))
    .set({ hour: 0, minute: 0, second: 0, millisecond: 0 });

  const packageStartsInTheFuture = Boolean(
    localizedStartOfPackageStartDate.diff(localizedStartOfDay).milliseconds > 0
  );
  if (packageStartsInTheFuture) {
    availability = PackageAvailability.NotYetAvailable;
    iconColour = "orange";
  }

  if (pkg.expires && pkg.endDate != null) {
    const localizedStartOfPackageEndDate = localizeDate(new Date(pkg.endDate))
      .set({ hour: 0, minute: 0, second: 0, millisecond: 0 });

    const packagedHasExpired = Boolean(
      localizedStartOfPackageEndDate.diff(localizedStartOfDay).milliseconds < 0
    );

    if (packagedHasExpired) {
      iconColour = "red";
      availability = PackageAvailability.NoLongerAvailable;
    }
  }

  if (isCapProductsPerReturnDateReleased && pkg.isOversold) {
    iconColour = "red";
    availability = PackageAvailability.SoldOut;
  }

  const isDisabled = isCapProductsPerReturnDateReleased && pkg.isOversold;

  const startDate = toFormattedDateString(new Date(pkg.startDate));
  const endDate = pkg.endDate ? toFormattedDateString(new Date(pkg.endDate)) : '';
  let availabilityText = '';

  if (availability === PackageAvailability.NotYetAvailable) {
    availabilityText = '[Not yet available]';
  } else if (availability === PackageAvailability.NoLongerAvailable) {
    availabilityText = '[No longer available]';
  }

  return (
    <Fragment>

      <div
        onClick={() => {
          if (isDisabled) return;
          props.onSelectPackage(props.package);
        }}
        data-testid={`package-item-${pkg.code}`}
        style={{
          ...styles.listItem,
          cursor: isDisabled ? "not-allowed" : "pointer",
          backgroundColor: props.selected == true ? "#eee" : "white",
          opacity: isDisabled ? 0.5 : 1,
          pointerEvents: isDisabled ? "none" : "auto"
        }}>
        <div style={{ ...flex.flexRows, justifyContent: "center", alignItems: "center" }}>
          <div style={flex.flexAuto}>
            <div>
              <IconButton disabled={true}>
                <FiBox color={iconColour} />
              </IconButton>
            </div>
          </div>
          <div style={{ ...flex.flexFill, textAlign: "left" }}>
            <div>
              <b>{pkg.name}</b>
            </div>

            {isCapProductsPerReturnDateReleased && availability == PackageAvailability.SoldOut ? (
              <div>
                <b style={{ color: 'red' }}>
                  {t("lists.packageOversold")}
                </b>
              </div>
            ) : (availability != PackageAvailability.available) && (
              <div>
                <small>
                  <Trans
                    i18nKey="lists.packageAvailability"
                    values={{
                      startDate: `<b>${startDate}</b>`,
                      endDate: endDate ? ` - <b>${endDate}</b>` : '',
                      availability: availabilityText ? `<b><i>${availabilityText}</i></b>` : ''
                    }}
                    components={{
                      b: <b />,
                      i: <i style={{ color: `${availability === PackageAvailability.NotYetAvailable ? 'orange' : 'red'}` }} />
                    }}
                  />
                </small>
              </div>
            )}



            {pkg.walkinRateType == ProductRate.adjusted && (
              <small>
                <Trans
                  i18nKey="lists.adjWalkInRate"
                  values={{
                    rate: pkg.walkinRate,
                    adjusted: '(Adjusted)'
                  }}
                  components={{
                    b: <b />,
                    span: <span style={{ color: "red" }} />
                  }}
                />

              </small>
            )}


            <div>
              <small style={{ color: "orange" }}>
                <b>{pkg.code}</b>
                {(pkg.ref != null && pkg.ref.length > 0) && (
                  <span> / <b>{pkg.ref}</b></span>
                )}
              </small>
            </div>

          </div>

          <div style={flex.flexAuto}>
            <div style={{ marginRight: 20 }}>
              <Fragment>{formatCurrency(calculatePackagePrice(props.package.products).dealPriceIncVat, session)}</Fragment>
            </div>
          </div>
        </div>
      </div >
    </Fragment>
  );
}

export default PackageListItemSelector;
